import React, { useState, useEffect } from "react";

import { THESPORTSDB_BASE } from "../data/leaguesConfig";

export default function HeadToHead({ homeTeam, awayTeam }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState({ home: 0, away: 0, draws: 0 });

  useEffect(() => {
    if (!homeTeam || !awayTeam) {
      setLoading(false);

      return;
    }

    const fetchH2H = async () => {
      try {
        let events = [];

        // Try both orderings
        const search1 = `${homeTeam}_vs_${awayTeam}`;
        const res1 = await fetch(
          `${THESPORTSDB_BASE}searchevents.php?e=${encodeURIComponent(search1)}`
        );
        const data1 = await res1.json();

        if (data1.event) events = [...events, ...data1.event];

        const search2 = `${awayTeam}_vs_${homeTeam}`;
        const res2 = await fetch(
          `${THESPORTSDB_BASE}searchevents.php?e=${encodeURIComponent(search2)}`
        );
        const data2 = await res2.json();

        if (data2.event) events = [...events, ...data2.event];

        // Deduplicate by idEvent
        const seen = new Set();

        events = events.filter((e) => {
          if (seen.has(e.idEvent)) return false;
          seen.add(e.idEvent);

          return true;
        });

        // Sort by date descending
        events.sort(
          (a, b) =>
            new Date(b.strTimestamp || b.dateEvent) -
            new Date(a.strTimestamp || a.dateEvent)
        );

        // Calculate record from last 10 meetings
        const recent = events.slice(0, 10);
        let homeWins = 0;
        let awayWins = 0;
        let draws = 0;

        recent.forEach((e) => {
          const hs = parseInt(e.intHomeScore) || 0;
          const as = parseInt(e.intAwayScore) || 0;

          if (hs === as) {
            draws++;

            return;
          }

          const winner = hs > as ? e.strHomeTeam : e.strAwayTeam;

          if (winner === homeTeam) homeWins++;
          else awayWins++;
        });

        setRecord({ home: homeWins, away: awayWins, draws });
        setMatches(recent.slice(0, 5));
      } catch (err) {
        console.error("H2H fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchH2H();
  }, [homeTeam, awayTeam]);

  if (loading) {
    return (
      <div className="px-6 py-8 text-center text-gray-400">
        Loading head-to-head...
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-gray-500">
        No head-to-head data available
      </div>
    );
  }

  const total = record.home + record.away + record.draws;

  return (
    <div className="px-6 py-8">
      {/* Record Summary */}
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-8">
        <h3 className="text-white/60 text-xs uppercase tracking-widest text-center mb-6">
          Last {total} Meetings
        </h3>
        <div className="flex items-center justify-center gap-8 sm:gap-12 md:gap-16">
          <div className="text-center">
            <div className="text-blue-400 text-3xl sm:text-4xl md:text-5xl font-bold">
              {record.home}
            </div>
            <div className="text-white/50 text-xs sm:text-sm mt-1 uppercase tracking-wide">
              {homeTeam}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-3xl sm:text-4xl md:text-5xl font-bold">
              {record.draws}
            </div>
            <div className="text-white/50 text-xs sm:text-sm mt-1 uppercase tracking-wide">
              Draws
            </div>
          </div>
          <div className="text-center">
            <div className="text-red-400 text-3xl sm:text-4xl md:text-5xl font-bold">
              {record.away}
            </div>
            <div className="text-white/50 text-xs sm:text-sm mt-1 uppercase tracking-wide">
              {awayTeam}
            </div>
          </div>
        </div>
      </div>

      {/* Last 5 Meetings */}
      <h3 className="text-white text-lg sm:text-xl font-semibold mb-4">
        Last {matches.length} Meetings
      </h3>
      <div className="space-y-3">
        {matches.map((match) => {
          const date = match.dateEvent || match.strTimestamp?.split("T")[0];
          const hs = parseInt(match.intHomeScore);
          const as = parseInt(match.intAwayScore);
          const hasScore = !isNaN(hs) && !isNaN(as);

          return (
            <div
              className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between gap-3"
              key={match.idEvent}
            >
              {/* Date */}
              <div className="text-white/40 text-xs sm:text-sm font-mono min-w-[80px] sm:min-w-[90px]">
                {date}
              </div>

              {/* Match Result */}
              <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-center text-sm sm:text-base">
                <span
                  className={`text-right flex-1 font-medium truncate ${
                    hasScore && hs > as ? "text-white" : "text-white/60"
                  }`}
                >
                  {match.strHomeTeam}
                </span>
                {hasScore ? (
                  <span className="text-white font-bold text-base sm:text-lg min-w-[50px] sm:min-w-[60px] text-center">
                    {hs} - {as}
                  </span>
                ) : (
                  <span className="text-white/30 text-sm min-w-[50px] sm:min-w-[60px] text-center">
                    vs
                  </span>
                )}
                <span
                  className={`text-left flex-1 font-medium truncate ${
                    hasScore && as > hs ? "text-white" : "text-white/60"
                  }`}
                >
                  {match.strAwayTeam}
                </span>
              </div>

              {/* League */}
              <div className="text-white/30 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[120px] text-right">
                {match.strLeague}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
